/**
 * Versioned program rules for the mock API (`PartnerRulesResponse`). Three published versions: 2 and 3
 * have been in force (3 is current), 4 is announced 27 days ahead. Bodies are Markdown, as stored.
 * The rule book in force must match `meMock.rules.currentVersion` ("3").
 */
import type { AcceptRulesRequest, AcceptRulesResponse, PartnerRulesResponse, RulesVersionView } from "@mfa-aff/shared";
import { formatJalali } from "@mfa-aff/ui";
import { ApiError } from "../api/client";
import { meMock } from "./me";

const DAY = 86_400_000;
const at = (days: number) => new Date(Date.now() + days * DAY).toISOString();

type Variant = { renewalSilver: string; walletBonus: boolean; autoPayout: boolean; shebaLock: boolean; couponSites: boolean };

/** A section of the rule book; a paragraph starting with «• » is a bullet. */
type Section = { id: string; title: string; body: string[] };

function sections(v: Variant): Section[] {
    return [
        {
            id: "scope",
            title: "۱. تعریف‌ها و دامنهٔ قوانین",
            body: [
                "این قوانین رابطهٔ میزبان‌فا و هر «بازاریاب» (بازاریابی که با لینک، کد معرف یا کد تخفیف خود مشتری معرفی می‌کند) را مشخص می‌کند. با فعال‌سازی حساب بازاریابی و پذیرش هر نسخه، همان نسخه برای سفارش‌هایی که از تاریخ اجرای آن پرداخت می‌شوند معتبر است.",
                "«مبلغ مشمول» مبلغ آیتم فاکتور پس از تخفیف و بدون مالیات است. «سرویس فعال» سرویسی است که از طرف شما خریده شده و در حال حاضر تعلیق یا لغو نشده است.",
                "همهٔ مبالغ در سیستم به ریال نگهداری و به تومان نمایش داده می‌شوند. تاریخ‌ها بر اساس تقویم رسمی ایران و ساعت تهران است.",
            ],
        },
        {
            id: "attribution",
            title: "۲. ثبت سفارش به نام بازاریاب",
            body: [
                "برای هر آیتم فاکتور فقط یک بازاریاب برنده است. ترتیب بررسی از بالا به پایین است و اولین موردی که برقرار باشد تصمیم را می‌گیرد:",
                "• تمدید سرویسی که قبلاً از طرف بازاریابی خریده شده، همیشه به همان بازاریاب می‌رسد.",
                "• کد تخفیف بازاریاب، اگر مشتری آن را در صفحهٔ پرداخت وارد کرده باشد.",
                "• کد معرف واردشده در صفحهٔ پرداخت.",
                "• آخرین لینک بازاریابی که مشتری در ۹۰ روز گذشته باز کرده است.",
                "• اتصال مشتری: مشتری‌ای که پیش‌تر از طرف بازاریابی خرید کرده، تا ۱۲ ماه به او متصل می‌ماند.",
                "خرید برای خودتان، با حساب خودتان یا حساب‌های مرتبط با شما، پورسانت ندارد. دلیل هر تصمیم کنار سفارش در پنل نوشته می‌شود و اگر فکر می‌کنید سفارشی مال شماست، تا ۶۰ روز می‌توانید «ادعا» ثبت کنید.",
            ],
        },
        {
            id: "commission",
            title: "۳. محاسبهٔ پورسانت و سطح‌ها",
            body: [
                "پورسانت از روی مبلغ مشمول و نرخ سطح شما در روز پرداخت فاکتور حساب می‌شود و تغییر نرخ‌های بعدی روی آن اثری ندارد.",
                `نرخ‌ها برای هر سطح در صفحهٔ «سطح‌ها و هدیه‌ها» آمده است. نرخ تمدید سطح نقره‌ای ${v.renewalSilver} است.`,
                "با کد تخفیف، تخفیفی که به مشتری می‌دهید از «سهم کد» سطح شما کم می‌شود: پورسانت = مبلغ مشمول × (سهم کد − درصد تخفیف). هزینهٔ میزبان‌فا در هر حال ثابت است.",
                "ثبت دامنه، افزودنی‌ها و شارژ کیف پول پورسانت ندارند. پیش‌پرداخت چندساله: ۱۲ ماه اول با نرخ فروش جدید و بقیه با نرخ تمدید.",
                "ارتقای سطح همان لحظهٔ رسیدن به شرط انجام می‌شود. تنزل فقط در بازبینی فصلی (اول فروردین، تیر، مهر و دی)، با یک فصل مهلت و حداکثر یک پله است. فقط فروش‌های تأییدشده پس از دورهٔ ضمانت شمرده می‌شوند.",
                ...(v.couponSites ? ["برای سایت‌هایی که کارشان انتشار کد تخفیف است، سهم کد ۵ واحد درصد پایین‌تر از جدول است."] : []),
            ],
        },
        {
            id: "hold",
            title: "۴. دورهٔ ضمانت و بررسی",
            body: [
                "پورسانت فروش جدید تا «۱۵ روز تقویمی پس از پرداخت» یا «پایان ۷ روز کاری»، هر کدام دیرتر باشد، در وضعیت «در انتظار» می‌ماند؛ برای تمدید این دوره ۷ روز است.",
                "در پایان دوره، اگر فاکتور هنوز پرداخت‌شده و سرویس فعال باشد، پورسانت خودکار «قابل برداشت» می‌شود. در غیر این صورت با ذکر دلیل باطل می‌شود.",
                "سفارش‌هایی که در بررسی امنیتی مشکوک تشخیص داده شوند، حداکثر ۶۰ روز «در حال بررسی» می‌مانند و نتیجه با دلیل به شما اعلام می‌شود.",
            ],
        },
        {
            id: "clawback",
            title: "۵. بازپس‌گیری",
            body: [
                "اگر مشتری پیش از تأیید پورسانت وجهش را پس بگیرد، پورسانت کامل حذف می‌شود.",
                "اگر پس از تأیید وجه برگردد، به همان نسبت از پورسانت کم می‌شود و از پورسانت‌های بعدی شما کسر می‌شود. موجودی ممکن است موقتاً منفی شود، ولی هرگز از شما پول نقد مطالبه نمی‌شود.",
            ],
        },
        {
            id: "payouts",
            title: "۶. تسویه",
            body: [
                v.walletBonus
                    ? "برداشت به کیف پول میزبان‌فا فوری است و ۵٪ هدیه روی مبلغ خالص دارد. اعتبار کیف پول برای خرید و تمدید سرویس است و به حساب بانکی برنمی‌گردد."
                    : "برداشت به کیف پول میزبان‌فا فوری است. اعتبار کیف پول برای خرید و تمدید سرویس است و به حساب بانکی برنمی‌گردد.",
                "برداشت بانکی فقط به شبای حسابی انجام می‌شود که به نام خود بازاریاب باشد؛ نام صاحب حساب از بانک استعلام و با کد ملی تطبیق داده می‌شود. حداقل برداشت بانکی سه برابر حداقل برداشت به کیف پول است.",
                ...(v.shebaLock ? ["اگر شبای تازه خودکار به نام شما تطبیق داده نشود، برای امنیت حساب تا ۴۸ ساعت تسویهٔ بانکی انجام نمی‌شود."] : []),
                ...(v.autoPayout
                    ? ["می‌توانید تسویهٔ خودکار تعریف کنید: هر وقت موجودی به مبلغ دلخواه رسید یا در روزهای مشخصی از ماه، با سهم دلخواه بین کیف پول و حساب بانکی."]
                    : []),
                "تسویه‌های بالای ۵۰ میلیون تومان با تأیید دو کارشناس مالی انجام می‌شود. برای هر تسویه رسید با شمارهٔ پیگیری صادر می‌شود.",
            ],
        },
        {
            id: "tax",
            title: "۷. مالیات",
            body: [
                "در صورتی که قانون یا سازمان امور مالیاتی کسر مالیات تکلیفی را لازم بداند، نرخ آن برای هر نوع بازاریاب اعلام و پیش از واریز از مبلغ کسر می‌شود و در رسید جداگانه نشان داده می‌شود.",
                "در حال حاضر نرخ مالیات تکلیفی برای بازاریاب‌های حقیقی صفر است. هرگونه تغییر دست‌کم ۳۰ روز زودتر اعلام می‌شود.",
            ],
        },
        {
            id: "prohibited",
            title: "۸. رفتارهای ممنوع",
            body: [
                "• خرید برای خودتان یا اطرافیان با هدف گرفتن پورسانت.",
                "• خرید کلمات کلیدی حاوی نام «میزبان‌فا» در تبلیغات کلیکی، یا ساخت صفحه و حسابی که خود را میزبان‌فا معرفی کند.",
                "• ارسال پیامک یا ایمیل انبوه بدون رضایت گیرنده.",
                "• وعدهٔ تخفیف یا امکاناتی که میزبان‌فا ارائه نمی‌کند، یا تحریف قیمت‌ها.",
                "• ترفندهایی که کوکی یا لینک شما را بدون کلیک واقعی مشتری ثبت کند.",
                "در صورت تخلف، پورسانت‌های مرتبط باطل می‌شود و در موارد تکراری حساب بازاریابی بسته می‌شود. تصمیم با ذکر دلیل اعلام می‌شود و قابل اعتراض است.",
            ],
        },
        {
            id: "changes",
            title: "۹. تغییر نرخ‌ها و قوانین",
            body: [
                "هر تغییر در نرخ‌ها یا این قوانین دست‌کم ۳۰ روز پیش از اجرا در همین صفحه، صندوق اعلان پنل و با پیامک اعلام می‌شود.",
                "تغییرات فقط روی سفارش‌هایی اثر دارد که از تاریخ اجرا به بعد پرداخت شوند؛ پورسانت‌هایی که قبلاً ثبت شده‌اند هرگز کم نمی‌شوند.",
                "اگر نسخهٔ جدید را نپذیرید، می‌توانید پیش از تاریخ اجرا از برنامهٔ بازاریابی خارج شوید و موجودی قابل برداشت خود را کامل دریافت کنید.",
            ],
        },
        {
            id: "termination",
            title: "۱۰. خروج از برنامه و حل اختلاف",
            body: [
                "هر کدام از طرفین می‌تواند عضویت در برنامهٔ بازاریابی را پایان دهد. پس از پایان، پورسانت‌های در انتظار طبق همین قوانین قطعی و موجودی قابل برداشت پرداخت می‌شود.",
                "اختلاف‌ها ابتدا از طریق «ادعا» در پنل و تیکت پشتیبانی بررسی می‌شوند. هر تصمیم مدیر همراه با دلیل در سیستم ثبت می‌شود.",
            ],
        },
    ];
}

/** The rule book as stored (Markdown): `## heading`, paragraphs, `- ` bullets. */
function markdown(v: Variant): string {
    return sections(v)
        .map((s) => [`## ${s.title}`, ...s.body.map((p) => (p.startsWith("• ") ? `- ${p.slice(2)}` : `\n${p}\n`))].join("\n"))
        .join("\n\n")
        .replace(/\n{3,}/g, "\n\n");
}

function version(p: { id: number; version: number; title: string; changeSummary: string | null; publishedDays: number; effectiveDays: number; variant: Variant }): RulesVersionView {
    const effectiveFrom = at(p.effectiveDays);
    return {
        id: p.id,
        version: p.version,
        title: p.title,
        bodyMd: markdown(p.variant),
        changeSummary: p.changeSummary,
        publishedAt: at(p.publishedDays),
        effectiveFrom,
        effectiveFromJalali: formatJalali(effectiveFrom),
    };
}

const announced = version({
    id: 7,
    version: 4,
    title: "نسخهٔ پاییز ۱۴۰۵",
    changeSummary: [
        "- نرخ تمدید سطح نقره‌ای از ۸٪ به ۹٪ افزایش می‌یابد.",
        "- نرخ کد برای سایت‌های انتشار کد تخفیف صریحاً ۵ واحد پایین‌تر اعلام شد.",
        "- بند «رفتارهای ممنوع» با مثال‌های روشن‌تر بازنویسی شد.",
    ].join("\n"),
    publishedDays: -3,
    effectiveDays: 27,
    variant: { renewalSilver: "۹٪", walletBonus: true, autoPayout: true, shebaLock: true, couponSites: true },
});

const inForce = version({
    id: 5,
    version: 3,
    title: "نسخهٔ تابستان ۱۴۰۵",
    changeSummary: [
        "- برداشت به کیف پول ۵٪ هدیه می‌گیرد.",
        "- تسویهٔ خودکار با آستانهٔ مبلغ یا روزهای ماه اضافه شد.",
        "- قفل ۴۸ ساعته پس از تغییر شبا، فقط وقتی شبا خودکار به نام بازاریاب تطبیق داده نشود.",
    ].join("\n"),
    publishedDays: -118,
    effectiveDays: -88,
    variant: { renewalSilver: "۸٪", walletBonus: true, autoPayout: true, shebaLock: true, couponSites: false },
});

const previous = version({
    id: 2,
    version: 2,
    title: "نسخهٔ زمستان ۱۴۰۴",
    changeSummary: null,
    publishedDays: -300,
    effectiveDays: -270,
    variant: { renewalSilver: "۸٪", walletBonus: false, autoPayout: false, shebaLock: false, couponSites: false },
});

const withoutBody = ({ bodyMd: _omit, ...rest }: RulesVersionView): Omit<RulesVersionView, "bodyMd"> => rest;

export function rulesMock(): PartnerRulesResponse {
    const rateEffective = at(27);
    return {
        current: structuredClone(inForce),
        upcoming: [structuredClone(announced)],
        history: [inForce, previous].map(withoutBody),
        upcomingRateChanges: [
            { version: 6, effectiveFrom: rateEffective, effectiveFromJalali: formatJalali(rateEffective), reason: "افزایش نرخ تمدید سطح نقره‌ای از ۸٪ به ۹٪." },
        ],
    };
}

export function acceptRulesMock(body: AcceptRulesRequest): AcceptRulesResponse {
    if (body.version !== meMock.rules.currentVersion && body.version !== meMock.rules.upcoming?.version) {
        throw new ApiError(409, "نسخهٔ قوانین تغییر کرده است؛ صفحه را دوباره باز کنید و نسخهٔ در حال اجرا را بپذیرید.", "version");
    }
    const acceptedAt = new Date().toISOString();
    meMock.rules.acceptedVersion = body.version;
    meMock.rules.acceptedAt = acceptedAt;
    meMock.rules.needsAcceptance = false;
    meMock.rules.mustAccept = false;
    if (meMock.rules.upcoming && body.version === meMock.rules.upcoming.version) meMock.rules.upcoming.accepted = true;
    return { acceptedVersion: body.version, acceptedAt };
}
